import { ReactNode, createContext, useContext, useEffect, useRef } from 'react'

import debounce from 'lodash/debounce'
import { createStore, useStore } from 'zustand'
import { shallow } from 'zustand/shallow'

const getIsMobile = () => {
  return typeof window !== 'undefined' ? window.innerWidth < 768 : undefined
}

const getIsLargeMobile = () => {
  return typeof window !== 'undefined'
    ? window.innerWidth >= 768 && window.innerWidth < 1112
    : undefined
}

export const useScreenSize = () => {
  const { isMobile, isLargeMobile, isMobileSSR } = useScreenSizeContext(
    (state) => ({
      isMobile: state.isMobile,
      isLargeMobile: state.isLargeMobile,
      isMobileSSR: state.isMobileSSR,
    }),
    shallow,
  )
  return { isMobile, isLargeMobile, isMobileSSR }
}

type ScreenSizeStore = {
  isMobileSSR?: boolean
  isMobile?: boolean
  isLargeMobile?: boolean
  setIsMobile: (isMobile: boolean) => void
  setIsLargeMobile: (isLargeMobile: boolean) => void
  setSSR: (isMobileSSR: boolean) => void
}

const useScreenSizeListener = () => {
  const { setIsMobile, setIsLargeMobile } = useScreenSizeContext(
    (state) => ({
      setSSR: state.setSSR,
      setIsMobile: state.setIsMobile,
      setIsLargeMobile: state.setIsLargeMobile,
    }),
    shallow,
  )
  useEffect(() => {
    const handleResize = debounce(() => {
      const isMobileVal = getIsMobile()
      const isLargeMobileVal = getIsLargeMobile()
      if (typeof isMobileVal === 'boolean') {
        setIsMobile(isMobileVal)
      }
      if (typeof isLargeMobileVal === 'boolean') {
        setIsLargeMobile(isLargeMobileVal)
      }
    }, 200)

    window.addEventListener('resize', handleResize, {
      passive: true,
    })

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])
}

export const ScreenSizeListener = () => {
  useScreenSizeListener()
  return null
}

const createScreenSizeStore = (initProps?: Partial<ScreenSizeStore>) => {
  const DEFAULT_PROPS: Partial<ScreenSizeStore> = {
    isMobile: getIsMobile(),
    isLargeMobile: getIsLargeMobile(),
  }
  return createStore<ScreenSizeStore>()((set) => ({
    ...DEFAULT_PROPS,
    ...initProps,
    setIsMobile: (isMobile: boolean) => set({ isMobile }),
    setIsLargeMobile: (isLargeMobile: boolean) => set({ isLargeMobile }),
    setSSR: (isMobileSSR: boolean) => set({ isMobileSSR }),
  }))
}
type ScreenSizeContextStore = ReturnType<typeof createScreenSizeStore>
export const ScreenSizeContext = createContext<ScreenSizeContextStore | null>(
  null,
)
export const ScreenSizeProvider = ({
  isMobileSSR,
  children,
}: {
  isMobileSSR?: boolean
  children?: ReactNode
}) => {
  const storeRef = useRef<ScreenSizeContextStore>()
  if (!storeRef.current) {
    storeRef.current = createScreenSizeStore({ isMobileSSR })
  }
  return (
    <ScreenSizeContext.Provider value={storeRef.current}>
      {children}
    </ScreenSizeContext.Provider>
  )
}

function useScreenSizeContext<T>(
  selector: (state: ScreenSizeStore) => T,
  equalityFn?: (a: T, b: T) => boolean,
): T {
  const store = useContext(ScreenSizeContext)
  if (!store) throw new Error('Missing ScreenSizeContext.Provider in the tree')
  return useStore(store, selector, equalityFn)
}
